import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  makeStyles,
} from '@material-ui/core';
import { Progress, ResponseErrorPanel } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import useAsync from 'react-use/lib/useAsync';
import { chartSummaryApiRef } from '../../api';
import { RequirePermission } from '@backstage/plugin-permission-react';
import {
  chartSummaryReadPermission,
  getEnvironmentCommonName,
  ApplicationResponse,
} from '@internal/plugin-dev-services-chart-summary-common';
import { PermissionDeniedCard } from '../ErrorCards';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import { NotFoundCard } from '../ErrorCards/ErrorCards';

const useStyles = makeStyles(theme => ({
  gridItemCard: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100% - 10px)', // for pages without content header
    marginBottom: '10px',
  },
  cardHeader: {
    padding: '16px 16px 16px 20px',
  },
  cardContentContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
  },
  columnContainer: {
    marginLeft: '8px',
    marginRight: '8px',
    paddingBlock: '16px',
    width: '-webkit-fill-available',
    display: 'flex',
    flexDirection: 'column',
  },
  fieldHeading: {
    textTransform: 'uppercase',
    overflow: 'hidden',
    fontSize: '10px',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    letterSpacing: '0.5px',
    marginTop: '0px',
    marginBottom: '2px',
  },
  fieldContent: {
    wordBreak: 'break-word',
    padding: '0px',
    margin: '0px',
  },
  hint: {
    fontSize: '9px',
    color: theme.palette.textSubtle,
    marginBottom: '12px',
  },
  emphasis: {
    fontSize: '14px',
    fontWeight: 'bolder',
  },
  hidden: {
    visibility: 'hidden',
  },
  warning: {
    color: theme.palette.status.warning,
  },
  ok: {
    color: theme.palette.status.ok,
  },
}));

const createField = ({
  classes,
  heading,
  content,
  isWarning,
  hint,
}: {
  classes: ClassNameMap;
  heading: string;
  content: React.JSX.Element;
  isWarning: boolean;
  hint?: string;
}) => {
  return (
    <div key={heading.toLowerCase().replaceAll(' ', '_')}>
      <h2
        className={`${classes.fieldHeading} ${
          isWarning ? classes.warning : classes.ok
        }`}
      >
        {heading}
      </h2>
      {content}
      <div
        data-testid="hint"
        className={`${classes.hint} ${isWarning && hint ? '' : classes.hidden}`}
      >
        <span className={`${classes.emphasis} ${classes.warning}`}>* </span>
        <span>{hint}</span>
      </div>
    </div>
  );
};

const DeploymentCardContent = ({
  classes,
  lifecycle,
  application,
}: {
  classes: ClassNameMap;
  lifecycle: string;
  application: ApplicationResponse;
}) => {
  const { environments, deploymentGates } = application;

  let allGatesMet = lifecycle === 'production';

  const lifecycleElement = createField({
    classes,
    heading: 'Lifecycle',
    content: <p className={classes.fieldContent}>{lifecycle}</p>,
    isWarning: lifecycle !== 'production',
    hint: `A production lifecycle is required to promote beyond dev`,
  });

  const envClassMap = new Map<string, React.JSX.Element[]>();
  for (const env of environments) {
    if (env.environment === 'pipeline') {
      continue;
    }

    const classEnvs = envClassMap.get(env.class);
    const commonName = getEnvironmentCommonName(env);
    const content = (
      <p key={commonName} className={classes.fieldContent}>
        {commonName}
      </p>
    );
    classEnvs ? classEnvs.push(content) : envClassMap.set(env.class, [content]);
  }

  const pipelineGateFields = deploymentGates.map(gate => {
    allGatesMet = allGatesMet && gate.met;
    return createField({
      classes,
      heading: gate.name,
      content: <p className={classes.fieldContent}>{gate.met.toString()}</p>,
      isWarning: !gate.met,
      hint: `${gate.name} is required to be ${gate.required} to promote beyond dev`,
    });
  });

  const environmentFields = ['dev', 'staging', 'production'].map(
    environmentClass => {
      const envs = envClassMap.get(environmentClass);
      return createField({
        classes,
        heading: `${environmentClass} environments`,
        content: (
          <div>
            {!envs?.length ? (
              <p className={classes.fieldContent}>none</p>
            ) : (
              envs
            )}
          </div>
        ),
        isWarning: environmentClass !== 'dev' && !allGatesMet,
      });
    },
  );

  return (
    <>
      <div key="environments" className={classes.columnContainer}>
        {lifecycleElement}
        {environmentFields}
      </div>
      <div key="features" className={classes.columnContainer}>
        {pipelineGateFields}
      </div>
    </>
  );
};

export function DeploymentGateCard() {
  const classes = useStyles();
  const { entity } = useEntity();
  const identityApi = useApi(identityApiRef);
  const chartSummaryApi = useApi(chartSummaryApiRef);
  const { value, loading, error } = useAsync(async () => {
    const { token } = await identityApi.getCredentials();

    if (!token) {
      throw new Error('Could not retrieve token');
    }
    const applicationId = entity.metadata.uid!;
    return chartSummaryApi.getApplication(applicationId);
  }, [identityApi, chartSummaryApi]);

  const lifecycle = entity?.spec?.lifecycle as string;

  return (
    <Card className={classes.gridItemCard}>
      <CardHeader className={classes.cardHeader} title="Deployment Criteria" />
      <Divider />
      <RequirePermission
        permission={chartSummaryReadPermission}
        errorPage={
          <PermissionDeniedCard className={classes.cardContentContainer} />
        }
      >
        <CardContent className={classes.cardContentContainer}>
          {loading && <Progress />}
          {error &&
            ((error as any).status === 404 ? (
              <NotFoundCard className={classes.cardContentContainer} />
            ) : (
              <ResponseErrorPanel error={error} />
            ))}
          {value && (
            <DeploymentCardContent
              classes={classes}
              lifecycle={lifecycle}
              application={value}
            />
          )}
        </CardContent>
      </RequirePermission>
    </Card>
  );
}
